
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ContactDemo from "../Dashboard/ContactDemo";
import {
  ArrowRight,
  BarChart3,
  BellRing,
  Bot,
  BriefcaseBusiness,
  Building2,
  ChartNoAxesCombined,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Clock3,
  FileSearch,
  Globe2,
  IndianRupee,
  Keyboard,
  LayoutDashboard,
  Megaphone,
  MessageCircle,
  MessageSquareMore,
  PackageCheck,
  PhoneCall,
  Rocket,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Store,
  Users,
  Utensils,
  WalletCards,
  Zap,
} from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CtaSection from "./CTASection";
import WhatsappMainOrdering from "../../images/LandingPageImage/Whatsapp Main Ordering.webp";
import gstImage from "../../images/LandingPageImage/Customer starts a conversation_1x.webp";
import addItemsImage from "../../images/LandingPageImage/The bot shares.webp";
import invoiceImage from "../../images/LandingPageImage/Customer selects.webp";
import whatsappImage from "../../images/LandingPageImage/Order reaches.webp";
import LogoScroll from "./LogoScroll";

const benefits = [
  {
    title: "Low commission on orders ",
    description: "Unlike food-delivery apps, you keep most of what you earn.",
  },
  {
    title: "No app download required for customers",
    description:
      "They already use WhatsApp, so ordering feels quick and simple.",
  },
  {
    title: "Ready in a day ",
    description:
      "Add your menu, connect your number, and start taking orders the same day.",
  },
];

const trustStats = [
  {
    value: 24,
    suffix: "×7",
    label: "Support",
    icon: Building2,
  },
  {
    value: 98,
    suffix: "%",
    label: "Message open rate",
    icon: MessageCircle,
  },
  {
    value: "No",
    suffix: "",
    label: "Setup Cost",
    icon: CircleDollarSign,
  },
  {
    value: 24,
    suffix: "×7",
    label: "Automated ordering",
    icon: Clock3,
  },
];

const problems = [
  {
    title: "Orders get buried",
    description: "Orders get lost in a busy, personal WhatsApp chat.",
    icon: MessageSquareMore,
  },
  {
    title: "Manual typing every day",
    description:
      "Staff spend hours every day typing out menus, prices and order confirmations.",
    icon: Keyboard,
  },
  {
    title: "Customers wait too long",
    description:
      "Customers wait too long for a reply and order from someone else instead.",
    icon: Clock3,
  },
  {
    title: "No searchable order record",
    description: "There is no simple record of who ordered what, and when.",
    icon: FileSearch,
  },
  {
    title: "No visibility into performance",
    description:
      "Owners cannot easily see how many orders came in or what sold the most",
    icon: ChartNoAxesCombined,
  },
];

const steps = [
  {
    title: "Customer starts a conversation",
    description:
      "Customer taps your WhatsApp ad, scans a QR code, or messages your business number.",
    image: gstImage,
  },
  {
    title: "The bot shares your catalogue",
    description:
      "Anlook's bot shares your menu right away — with categories, photos and prices.",
    image: addItemsImage,
  },
  {
    title: "Customer selects and pays",
    description:
      "Customer picks items, confirms the order, and pays using UPI or a payment link, inside the same chat.",
    image: invoiceImage,
  },
  {
    title: "Order reaches your dashboard",
    description:
      "The order goes straight into your Anlook dashboard, ready to confirm and prepare.",
    image: whatsappImage,
  },
];

const features = [
  {
    title: "Digital Menu Builder",
    description:
      "Add as many products, categories, photos and prices as you need, and update them anytime.",
    icon: ShoppingBag,
  },
  {
    title: "Automated Order-Taking Bot",
    description:
      "Answers common questions and takes complete, accurate orders without manual typing.",
    icon: Bot,
  },
  {
    title: "Instant Payments",
    description:
      "Customers pay using UPI or a payment link, right inside the chat — no separate payment app needed.",
    icon: WalletCards,
  },
  {
    title: "One Order Dashboard ",
    description:
      "Every order is stored in one place, with clear status — new, confirmed, preparing, or dispatched.",
    icon: LayoutDashboard,
  },
  {
    title: "Broadcast and Reminders",
    description:
      "Send offers and reminder messages to past customers in bulk, whenever you want.",
    icon: Megaphone,
  },
  {
    title: "Shared Team Inbox ",
    description:
      "Your whole team can reply from the same WhatsApp number without mixing up conversations.",
    icon: Users,
  },
  {
    title: "Automated Order Updates",
    description:
      "Customers get order confirmations and status updates on their own, without your team typing anything.",
    icon: BellRing,
  },
  {
    title: "Analytics and Order Reports",
    description:
      "Track order value, product performance, customer activity and daily sales trends.",
    icon: BarChart3,
  },
];

const reasons = [
  {
    title: "Get more orders ",
    description:
      "Quick replies and an easy menu turn more chats into paying customers.",
    icon: Rocket,
  },
  {
    title: "Save staff time ",
    description:
      "Automation takes over repeated order-taking, so your team can focus on service.",
    icon: Zap,
  },
  {
    title: "Stop missing orders ",
    description: "Every message is saved and tracked in one dashboard.",
    icon: ShieldCheck,
  },
  {
    title: "Keep more of your earnings ",
    description:
      "Low commission on orders.",
    icon: IndianRupee,
  },
  {
    title: "Grows with your business ",
    description: "Works for one shop or for many branches.",
    icon: Globe2,
  },
];

const industries = [
  {
    title: "Restaurants & Cafes",
    description:
      "Accept daily food orders, show menus and collect payments directly on WhatsApp.",
    icon: Utensils,
  },
  {
    title: "Retail & Kirana Stores",
    description:
      "Sell grocery, household and retail products through a searchable digital catalogue.",
    icon: Store,
  },
  {
    title: "D2C & E-commerce Brands",
    description:
      "Offer a complete catalogue and checkout journey without building a full website.",
    icon: ShoppingCart,
  },
  {
    title: "Cloud Kitchens",
    description:
      "Manage high-volume food orders and send live preparation updates automatically.",
    icon: PackageCheck,
  },
  {
    title: "Service Businesses",
    description:
      "Accept booking-style orders for salons, bakeries, pharmacies, repairs and local services.",
    icon: BriefcaseBusiness,
  },
  {
    title: "Multi-Branch Businesses",
    description:
      "Manage teams, outlets, product availability and customer conversations centrally.",
    icon: Building2,
  },
];

const faqs = [
  {
    question: "Do my customers need to install an app?",
    answer:
      "No, customers place orders through WhatsApp, which they already use. No new application or account is required.",
  },
  {
    question: "Can I add my full menu or product catalogue?",
    answer:
      "Yes, you can add unlimited categories, items, images, prices, variants and availability, and update them whenever required.",
  },
  {
    question: "How do customers pay?",
    answer:
      "Customers can pay through UPI or a secure payment link shared directly inside their WhatsApp conversation.",
  },
  {
    question: "How long does setup take?",
    answer:
      "Most businesses can go live on the same day after connecting their WhatsApp number and uploading their catalogue.",
  },
  {
    question: "Can multiple staff members reply to orders?",
    answer:
      "Yes, the shared inbox lets your team manage customer conversations and orders from one business number.",
  },
  {
    question: "Will I receive new order notifications?",
    answer:
      "Yes, every new order can trigger an instant alert so your staff can confirm and prepare it immediately.",
  },
  {
    question: "Can I send offers to previous customers?",
    answer:
      "Yes, you can create broadcast campaigns, festive offers, product alerts and cart-reminder messages.",
  },
];

const SectionHeading = ({
  badge,
  title,
  highlightedText,
  description,
  align = "center",
}) => {
  const isLeft = align === "left";

  return (
    <div className={isLeft ? "max-w-3xl" : "mx-auto max-w-3xl text-center"}>
      {badge && (
        <div
          className={`mb-5 inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-4 py-2 text-sm font-semibold text-green-700 ${
            isLeft ? "" : "mx-auto"
          }`}
        >
          <Sparkles className="h-4 w-4" />
          {badge}
        </div>
      )}

      <h2 className="montserrat-font-1 text-3xl font-extrabold leading-tight tracking-tight text-slate-950 sm:text-4xl lg:text-[42px]">
        {title}{" "}
        {highlightedText && (
         <span className="montserrat-font-1 md:block lg:inline text-green-500 whitespace-nowrap">
  {highlightedText}
</span>
        )}
      </h2>

      {description && (
        <p className="montserrat-font-2 mt-5 text-base leading-7 text-slate-600 sm:text-lg">
          {description}
        </p>
      )}
    </div>
  );
};

const WhatsAppOrderService = () => {
    const navigate = useNavigate();
    const location = useLocation();
  const [openFaq, setOpenFaq] = useState(0);
  const [openDemo, setOpenDemo] = useState(false);

    useEffect(() => {
      if (location.hash === "#startfree-trial") {
        setOpenDemo(true);
      }
    }, [location.hash]);

    const handleStartFreeTrial = () => {
      navigate("/services/whatsapp/#startfree-trial");
    };

    const handleCloseDemo = () => {
      setOpenDemo(false);

      if (location.hash === "#startfree-trial") {
        navigate("/services/whatsapp/", {
          replace: true,
        });
      }
    };

  return (
    <>
      <Navbar />

      <main className="overflow-hidden bg-white text-slate-900">
        {/* HERO */}
        <section className="relative isolate overflow-hidden pt-[85px]">
          <div className="absolute inset-0 -z-20 bg-gradient-to-br from-green-50 via-white to-emerald-50" />

          <div className="absolute -left-32 top-20 -z-10 h-72 w-72 rounded-full bg-green-200/50 blur-3xl sm:h-96 sm:w-96" />
          <div className="absolute -right-40 top-0 -z-10 h-80 w-80 rounded-full bg-green-300/30 blur-3xl sm:h-[500px] sm:w-[500px]" />

          <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-4 py-10 sm:px-6 sm:py-14 md:px-8 lg:min-h-[calc(100vh-75px)] lg:grid-cols-[1.06fr_0.94fr] lg:gap-14 lg:px-20 lg:py-0">
            {/* Left */}
            <div className="order-1 text-center lg:order-1 lg:text-left">
              <h1 className="montserrat-font-1 text-[28px] font-semibold leading-snug tracking-tight text-slate-950 sm:text-[36px] md:text-[42px] lg:max-w-4xl lg:text-[46px]">
                Take orders directly on{" "}
                <span className="relative inline-block text-green-500 whitespace-nowrap">
                  WhatsApp
                  <span className="absolute -bottom-1 left-0 h-1.5 w-full rounded-full bg-green-300/70" />
                </span>
                <span className="sm:inline">
                  {" "}
                  No app.
                  <br className="sm:hidden" />
                  <span className="hidden sm:inline"> </span>
                  No website.
                </span>
              </h1>

              <p className="montserrat-font-2 mx-auto mt-6 max-w-2xl text-[15px] leading-7 text-slate-600 sm:text-base lg:mx-0 lg:text-lg">
                Customers can see your menu, place an order, and pay — all
                inside WhatsApp. Get started in minutes with Anlook.
              </p>

              {/* Benefits */}
              <div className="mt-8 space-y-4">
                {benefits.map((benefit) => (
                  <div
                    key={benefit.title}
                    className="flex items-start gap-3 text-left"
                  >
                    <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                      <Check className="h-4 w-4" strokeWidth={3} />
                    </span>

                    <p className="montserrat-font-2 text-sm leading-6 text-slate-600 sm:text-base">
                      <span className="font-bold text-slate-950">
                        {benefit.title}
                      </span>
                      {" — "}
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>

              {/* Buttons */}
              <div className="montserrat-font-2 mt-10 flex flex-row gap-3">
                <button
                  type="button"
                  onClick={handleStartFreeTrial}
                  className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-[#004d40] px-4 text-sm font-bold text-white shadow-lg transition hover:scale-[1.02] sm:h-14 sm:px-7 sm:text-base"
                >
                  Start Free Trial
                  <ArrowRight className="h-5 w-5" />
                </button>

                <button
                  type="button"
                  onClick={handleStartFreeTrial}
                  className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-bold text-white shadow-sm transition hover:scale-[1.02] sm:h-14 sm:px-7 sm:text-base"
                >
                  Book a Free Demo
                  <PhoneCall className="h-5 w-5" />
                </button>
              </div>

              {/* Bottom */}
              <div className="montserrat-font-2 mt-6 flex flex-wrap justify-center gap-x-5 gap-y-3 text-sm text-slate-500 lg:justify-start">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  No credit card
                </span>

                <span className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Same-day setup
                </span>

                <span className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Low commission
                </span>
              </div>
            </div>

            {/* Right Image */}
            <div className="order-2 mx-auto w-full max-w-xs sm:max-w-md md:max-w-lg lg:order-2 lg:max-w-[550px]">
              <div className="relative">
                <div className="absolute -inset-5 -z-10 rounded-[40px] bg-green-300/20 blur-2xl" />

                <div className="overflow-hidden rounded-[30px] border border-green-100 bg-[#01c37d] p-3 shadow-[0_30px_90px_rgba(22,163,74,0.18)]">
                  <img
                    src={WhatsappMainOrdering}
                    alt="WhatsApp Main Ordering"
                    className="h-auto w-full rounded-[24px] object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST BAR */}
        <section className="bg-white px-4 py-6 sm:px-6 sm:py-12 lg:px-20 lg:py-10">
          <div className="mx-auto max-w-7xl">
            <div className="relative overflow-hidden rounded-[24px] lg:rounded-[28px] stats-wrap px-4 py-6 shadow-lg sm:px-6 sm:py-8 lg:px-8 lg:py-10">
              {/* Background Glow */}
              <div className="pointer-events-none absolute -left-20 -top-24 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-28 -right-16 h-64 w-64 rounded-full bg-green-600/20 blur-3xl" />

              <div className="relative grid grid-cols-2 lg:grid-cols-4">
                {trustStats.map((stat, index) => {
                  const Icon = stat.icon;

                  return (
                    <div
                      key={stat.label}
                      className={`flex min-h-[120px] sm:min-h-[140px] lg:min-h-[160px]
                flex-col items-center justify-center
                px-3 py-5 sm:px-5 lg:px-6
                text-center text-white
                ${index % 2 !== 0 ? "border-l border-white/20" : ""}
                ${index >= 2 ? "border-t border-white/20 lg:border-t-0" : ""}
                ${index > 0 ? "lg:border-l lg:border-white/20" : ""}`}
                    >
                      {/* Icon */}
                      <div className="mb-3 flex h-10 w-10 sm:h-12 sm:w-12 lg:h-14 lg:w-14 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                        <Icon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-white" />
                      </div>

                      {/* Counter */}
                      <p className="montserrat-font-1 text-[24px] font-extrabold tracking-tight sm:text-3xl lg:text-4xl">
                        {stat.prefix}
                        {stat.value}
                        {stat.suffix}
                      </p>

                      {/* Label */}
                      <p className="montserrat-font-2 mt-2 max-w-[170px] text-[11px] leading-5 text-white/85 sm:text-sm lg:text-[16px]">
                        {stat.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* PROBLEM SECTION */}
        <section className="bg-white px-4 sm:px-6 sm:py-10 lg:px-20 ">
          <div className="mx-auto max-w-7xl">
            {/* Heading */}
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="montserrat-font-1 text-3xl font-extrabold leading-tight tracking-tight text-slate-950 sm:text-4xl lg:text-[42px]">
                Manual WhatsApp ordering{" "}
                <span className="text-green-500">breaks down as you grow</span>
              </h2>

              <p className="montserrat-font-2 mx-auto mt-5 max-w-[790px] text-base leading-7 text-slate-600 sm:text-lg">
                Most small businesses still take WhatsApp orders by hand —
                typing menus, forgetting follow-ups, and losing track of
                payments. As orders grow, this becomes hard to manage and costs
                money.
              </p>
            </div>

            {/* 2 LEFT CARDS + 3 RIGHT CARDS */}
            {/* Mobile + iPad */}
            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:hidden">
              {problems.map((problem, index) => {
                const Icon = problem.icon;

                return (
                  <article
                    key={problem.title}
                    className={`group flex flex-col sm:flex-row min-h-[180px] items-start sm:items-center gap-5 rounded-[24px] border border-slate-200 bg-white p-5 sm:p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-green-300 hover:shadow-xl hover:shadow-green-100
          ${
            index === 4
              ? "md:col-span-2 md:max-w-[48%] md:w-full md:justify-self-center"
              : ""
          }`}
                  >
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-green-100 bg-green-50 text-green-500 transition duration-300 group-hover:bg-green-500 group-hover:text-white">
                      <Icon className="h-7 w-7" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="montserrat-font-1 text-[18px] font-semibold leading-snug text-[#212529]">
                        {problem.title}
                      </h3>

                      <p className="montserrat-font-2 mt-2 text-[15px] leading-relaxed text-slate-600">
                        {problem.description}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* Desktop */}
            <div className="mt-12 hidden lg:grid gap-5">
              {/* Top Row */}
              <div className="grid gap-5 lg:grid-cols-3">
                {problems.slice(0, 3).map((problem) => {
                  const Icon = problem.icon;

                  return (
                    <article
                      key={problem.title}
                      className="group flex min-h-[180px] items-center gap-5 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-green-300 hover:shadow-xl hover:shadow-green-100"
                    >
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-green-100 bg-green-50 text-green-500 transition duration-300 group-hover:bg-green-500 group-hover:text-white">
                        <Icon className="h-7 w-7" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="montserrat-font-1 text-[18px] font-semibold leading-snug text-[#212529]">
                          {problem.title}
                        </h3>

                        <p className="montserrat-font-2 mt-2 text-[15px] leading-relaxed text-slate-600">
                          {problem.description}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Bottom Row */}
              <div className="grid grid-cols-12 gap-5">
                {problems.slice(3, 5).map((problem, index) => {
                  const Icon = problem.icon;

                  return (
                    <article
                      key={problem.title}
                      className={`group col-span-6 ${
                        index === 0 ? "col-start-1" : "col-start-7"
                      } flex min-h-[180px] items-center gap-5 rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-green-300 hover:shadow-xl hover:shadow-green-100`}
                    >
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-green-100 bg-green-50 text-green-500 transition duration-300 group-hover:bg-green-500 group-hover:text-white">
                        <Icon className="h-7 w-7" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="montserrat-font-1 text-[18px] font-semibold leading-snug text-[#212529]">
                          {problem.title}
                        </h3>

                        <p className="montserrat-font-2 mt-2 text-[15px] leading-relaxed text-slate-600">
                          {problem.description}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

    <LogoScroll/>

        {/* HOW IT WORKS */}
        <section className="bg-slate-50 px-5 py-20 sm:px-8 lg:px-20 lg:py-16">
          <div className="mx-auto max-w-[1200px]">
            <SectionHeading
              title="From first message to"
              highlightedText="confirmed order"
              description="Anlook removes manual, back-and-forth messaging. From the first message to the final order, everything happens on its own."
            />

            <div className="relative mt-16 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              <div className="absolute left-[12%] right-[12%] top-12 hidden h-px bg-green-200 lg:block" />

              {steps.map((step) => {
                const Icon = step.icon;

                return (
                  <article
                    key={step.step}
                    className="group overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-green-100"
                  >
                    {/* Image */}
                    <div className="relative h-60 overflow-hidden bg-slate-100">
                      <img
                        src={step.image}
                        alt={step.title}
                        className="h-full w-full object-fill transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>

                    {/* Content */}
                    <div className="p-7">
                      <h3 className="montserrat-font-1 text-[22px] font-bold leading-snug text-slate-900">
                        {step.title}
                      </h3>

                      <p className="montserrat-font-2 mt-3 text-[15px] leading-7 text-slate-600">
                        {step.description}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="bg-white px-5 py-20 sm:px-8 lg:px-20 lg:py-16">
          <div className="mx-auto mt-3 max-w-[1200px]">
            <SectionHeading
              title="Everything required to manage"
              highlightedText="WhatsApp orders"
              description="Create your catalogue, automate conversations, collect payments and manage every order from one central platform."
            />

            <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature) => {
                const Icon = feature.icon;

                return (
                  <article
                    key={feature.title}
                    className="group rounded-3xl border border-slate-200 bg-white p-7 transition duration-300 hover:-translate-y-1 hover:border-green-300 hover:shadow-xl hover:shadow-green-100/60"
                  >
                    <div className="flex h-13 w-13 h-14 w-14 items-center justify-center rounded-2xl bg-green-100 text-green-600 transition group-hover:bg-green-400 group-hover:text-white mx-auto">
                      <Icon className="h-7 w-7" />
                    </div>

                    <h3 className="montserrat-font-1 mt-6 text-xl text-center font-bold text-slate-950">
                      {feature.title}
                    </h3>

                    <p className="montserrat-font-2 mt-3 text-sm leading-6 text-center text-slate-600">
                      {feature.description}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* WHY ANLOOK */}
        <section className="relative overflow-hidden stats-wrap px-4 py-12 text-white sm:px-6 sm:py-16 lg:px-20 lg:py-20">
          <div className="absolute -left-24 top-10 h-80 w-80 rounded-full blur-3xl" />
          <div className="absolute -right-20 bottom-0 h-96 w-96 rounded-full blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-20">
            {/* LEFT CONTENT */}
            <div className="text-center lg:text-left">
              <h2 className="montserrat-font-1 text-3xl font-extrabold leading-tight sm:text-4xl lg:text-[42px]">
                Built for how Indian businesses{" "}
                <span className="whitespace-nowrap text-green-400">
                  actually sell
                </span>
              </h2>

              <p className="montserrat-font-2 mx-auto mt-6 max-w-xl text-base leading-7 text-white sm:text-lg lg:mx-0">
                Anlook is built the way Indian businesses actually sell — over
                chat, with customers who expect a quick reply.
              </p>

              <button
                type="button"
                onClick={handleStartFreeTrial}
                className="montserrat-font-2 mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#004d40] px-7 py-3.5 font-bold text-white transition hover:bg-[#00695c] sm:w-auto"
              >
                Get Started Free
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>

            {/* RIGHT SIDE */}
            <div className="overflow-hidden rounded-[24px]">
              {reasons.map((reason, index) => {
                const number = String(index + 1).padStart(2, "0");

                return (
                  <article
                    key={reason.title}
                    className="group flex items-center gap-4 border-b border-white/15 px-4 py-5 last:border-b-0 sm:gap-6 sm:px-6 sm:py-6 lg:min-h-[145px] lg:gap-8 lg:px-10"
                  >
                    {/* NUMBER */}
                    <div className="flex w-14 shrink-0 items-center justify-center sm:w-20 lg:w-[110px]">
                      <span className="reason-step-number text-[38px] font-black leading-none text-transparent transition-all duration-300 sm:text-[52px] lg:text-[70px]">
                        {number}
                      </span>
                    </div>

                    {/* CONTENT */}
                    <div className="min-w-0 flex-1">
                      <h3 className="montserrat-font-1 text-base font-extrabold text-white sm:text-lg lg:text-xl">
                        {reason.title}
                      </h3>

                      <p className="montserrat-font-2 mt-2 text-sm leading-6 text-white sm:text-base sm:leading-7">
                        {reason.description}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* WHO IT IS FOR */}
        <section className="bg-white px-5 py-20 sm:px-8 lg:px-20 lg:py-16">
          <div className="mx-auto max-w-[1200px]">
            <SectionHeading
              title="Made for businesses that sell"
              highlightedText="through conversations"
              description="Whether you sell food, products or services, Anlook gives customers an easier way to discover, order and pay."
            />

            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {industries.map((industry) => {
                const Icon = industry.icon;

                return (
                  <article
                    key={industry.title}
                    className="group rounded-3xl border border-slate-200 bg-white p-7 transition duration-300 hover:border-green-300 hover:bg-green-50/50 hover:shadow-xl hover:shadow-green-100/50"
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100 text-green-600 transition group-hover:bg-green-400 group-hover:text-white">
                      <Icon className="h-7 w-7" />
                    </div>

                    <h3 className="montserrat-font-1 mt-6 text-xl font-bold text-slate-950">
                      {industry.title}
                    </h3>

                    <p className="montserrat-font-2 mt-3 leading-7 text-slate-600">
                      {industry.description}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white px-5 font-sans sm:px-8 lg:px-20">
          <div className="mx-auto max-w-[760px]">
            {/* HEADING */}
            <div className="mx-auto mb-10 max-w-[760px] text-center">
              <h2 className="montserrat-font-1 text-[26px] font-semibold leading-snug tracking-normal text-[#212529] sm:text-[32px] md:text-[36px] lg:text-[42px]">
                Frequently Asked Questions
              </h2>

              <p className="montserrat-font-2 mx-auto mt-4 max-w-[680px] text-[14px] font-normal leading-relaxed text-slate-600 sm:text-[15px] md:text-[16px]">
                Everything you need to know before launching your automated
                WhatsApp ordering experience.
              </p>
            </div>

            {/* FAQ LIST */}
            <div className="space-y-4">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index;

                return (
                  <article
                    key={faq.question}
                    className="overflow-hidden rounded-xl bg-[#F1F5F5] transition duration-300"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left"
                    >
                      <span
                        className={`montserrat-font-2 text-[16px] leading-snug text-[#0C4D45] md:text-[18px] ${
                          isOpen ? "font-semibold" : "font-normal"
                        }`}
                      >
                        {faq.question}
                      </span>

                      <ChevronDown
                        className={`h-5 w-5 shrink-0 text-[#212529] transition-transform duration-300 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    <div
                      className={`grid transition-all duration-300 ease-in-out ${
                        isOpen
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="montserrat-font-2 px-5 pb-5 text-[14px] font-normal leading-relaxed text-[#262728] sm:text-[15px]">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <CtaSection onStartFreeTrial={handleStartFreeTrial} />
      </main>
      <Footer />
      <ContactDemo isOpen={openDemo} onClose={handleCloseDemo} />
    </>
  );
};

export default WhatsAppOrderService;